import React from 'react';
import { Container, Icon, Paper, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useAuthContext from '../../hooks/useAuthContext';
import { DataImport, FarmLogs, LabelPrintTasks, SlaughterImportHistory } from '../../components';
import { useTranslation } from 'react-i18next';
import Divider from '@mui/material/Divider';
import UserRole from '../../constants/user-role';
import { FeatureType, useFeatures } from '../../hooks/features';

const SystemData: React.FC<any> = () => {
  const { option } = useParams<{ option: string }>();
  const styles = sxStyles(useTheme());
  const { state } = useAuthContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasFeature } = useFeatures();

  const hasSlaughterImportHistory = hasFeature(FeatureType.VIEW_SLAUGHTER_IMPORT_HISTORY);

  const handleTabChange = (_event: React.ChangeEvent<object>, newValue: number) => {
    const routes = ['/system/logs', '/system/labels', '/system/data-import'];

    if (hasSlaughterImportHistory) {
      routes.push('/system/slaughter-import-history');
    }

    if (routes[newValue]) {
      navigate(routes[newValue]);
    }
  };

  const getTabIndex = (): number => {
    if (option && option === 'logs') {
      return 0;
    }

    if (option && option === 'labels') {
      return 1;
    }

    if (option && option === 'data-import') {
      return 2;
    }

    if (option && option === 'slaughter-import-history' && hasSlaughterImportHistory) {
      return 3;
    }

    return 0;
  };

  React.useEffect(() => {
    const tabIndex = getTabIndex();
    setTab(tabIndex);
  }, [option]);

  const [tab, setTab] = React.useState(getTabIndex());

  return state.role === UserRole.manager || state.role === UserRole.admin ? (
    <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container>
            <Grid item container md={4} xs={12} spacing={1}>
              <Grid item>
                <Icon fontSize="large" color="primary">
                  {'data_usage'}
                </Icon>
              </Grid>
              <Grid item>
                <Typography component="h2" variant="h6" color="primary">
                  {t('f-settings-sys-data')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid container direction="column" sx={styles.mt1}>
            <Tabs value={tab} onChange={handleTabChange}>
              <Tab label={t('f-logs')} id="logsTab" />
              <Tab label={t('f-settings-labels')} id="labelsTab" />
              <Tab label={t('f-settings-data-import')} id="dataImportTab" />
              {hasSlaughterImportHistory && (
                <Tab label={t('f-slaughter-import-history')} id="slaughterImportHistoryTab" />
              )}
            </Tabs>
            <Divider />
          </Grid>

          <Paper sx={styles.paper} elevation={1}>
            <Grid container>
              {tab === 0 && (
                <Grid item xs={12} md={12}>
                  <FarmLogs />
                </Grid>
              )}
              {tab === 1 && (
                <Grid item xs={12} md={12}>
                  <LabelPrintTasks />
                </Grid>
              )}
              {tab === 2 && (
                <Grid item xs={12} md={12}>
                  <DataImport />
                </Grid>
              )}
              {tab === 3 && hasSlaughterImportHistory && (
                <Grid item xs={12} md={12}>
                  <SlaughterImportHistory />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  ) : (
    <Navigate to={{ pathname: '/dashboard' }} />
  );
};

export default SystemData;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column',
      minHeight: 200
    },
    spinnerBox: {
      display: 'flex',
      justifyContent: 'center'
    },
    formControl: {
      margin: theme.spacing(1)
    },
    mt1: {
      marginTop: theme.spacing(1)
    }
  }) as const;
